import { LayoutTemplate } from 'lucide-react'
import type { SplitButtonMenuItem, SplitButtonAdminIndicator } from '../components/SplitButton'
import type { UserContextType } from '../hooks/useUser'

// Temporarily hide the "Start with a template" affordance everywhere (the
// Create-App SplitButton dropdown). Templates work is paused, so we suppress
// the trigger while keeping all the code — the TemplateGalleryModal, the
// templates tab/filter, and these helpers stay intact. Flip back to true to
// restore the dropdown in one place. Typed `boolean` (not a literal) so the
// early return below isn't flagged as unreachable.
const TEMPLATE_BUTTON_ENABLED: boolean = false

/**
 * Build the templates-related SplitButton props based on the current user's
 * admin status and the platform `templatesVisibleToUsers` flag.
 *
 * - Non-admins with flag OFF get no template menu item (chevron hides).
 * - Admins with flag OFF get the menu item + a red indicator.
 * - Everyone with flag ON gets the menu item, no indicator.
 *
 * While TEMPLATE_BUTTON_ENABLED is false this always returns an empty menu, so
 * every "Create App" SplitButton renders as a plain button (no chevron).
 */
export function buildTemplateSplitButtonProps(
  user: UserContextType,
  onOpenGallery: () => void,
): { menuItems: SplitButtonMenuItem[]; adminIndicator?: SplitButtonAdminIndicator } {
  if (!TEMPLATE_BUTTON_ENABLED) {
    return { menuItems: [] }
  }
  return {
    menuItems: user.features.templatesVisibleToMe
      ? [
          {
            label: 'Start with a template',
            description: 'Pre-built apps ready to customize',
            icon: <LayoutTemplate className="w-4 h-4" />,
            onClick: onOpenGallery,
          },
        ]
      : [],
    adminIndicator:
      user.isAdmin && !user.features.templatesVisibleToUsers
        ? { active: true, tooltip: 'Templates are hidden from regular users — only admins and allowlisted testers see them' }
        : undefined,
  }
}
